import {ApiClient} from '@hconnect/apiclient'
import axios, {AxiosError} from 'axios'
import {useCallback, useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {EquipmentData, EquipmentIdType, EquipmentPlain} from '../types'

const ITEMS_PER_RESPONSE = 100

type Payload = {
  searchTerm?: string
  plantId: string
  idType?: EquipmentIdType
}

interface UseEquipmentNumberSearchOptions<T> {
  enabled?: boolean
  select?: (data: EquipmentPlain[]) => T
  onSuccess?: (data: T) => void
  onError?: (error: AxiosError) => void
}

export const useEquipmentNumberSearchQuery = <T = EquipmentPlain[]>(
  {searchTerm, idType, plantId}: Payload,
  options?: UseEquipmentNumberSearchOptions<T>
) => {
  const {
    i18n: {language}
  } = useTranslation()
  const {enabled = true, select, onSuccess, onError} = options || {}

  const [data, setData] = useState<T | undefined>(undefined)
  const [error, setError] = useState<AxiosError | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const handleFetchEquipmentNumbers = useCallback(async () => {
    if (!plantId || !searchTerm || searchTerm.length < 2 || !enabled) {
      if (searchTerm && searchTerm.length < 2) {
        const emptyResult = select ? select([]) : ([] as T)
        setData(emptyResult)
        setError(null)
      }
      return
    }

    setIsLoading(true)

    try {
      const path = `/plantconfig/${plantId}/equipmentnumbers`
      const params = {
        params: {
          searchPattern: searchTerm,
          itemsPerResponse: ITEMS_PER_RESPONSE,
          language,
          idType
        }
      }

      const response = await ApiClient.axiosInstance.get<EquipmentPlain[]>(path, params)

      setData(() => {
        const transformedData = select ? select(response.data) : (response.data as T)

        onSuccess?.(transformedData)
        setError(null)
        return transformedData
      })
    } catch (error) {
      if (axios.isAxiosError(error)) {
        setError(error)
        onError?.(error)
      } else {
        console.error('Unexpected error:', error)
      }
    } finally {
      setIsLoading(false)
    }
  }, [plantId, searchTerm, idType, language, enabled, select, onSuccess, onError])

  useEffect(() => {
    if (enabled) {
      void handleFetchEquipmentNumbers()
    }
  }, [enabled, handleFetchEquipmentNumbers])

  return {
    data: data as T,
    error,
    isLoading,
    refetch: handleFetchEquipmentNumbers
  }
}

interface UseSpecificEquipmentNumberSearchOptions<T> {
  enabled?: boolean
  select?: (data: EquipmentPlain[]) => T
  onSuccess?: (data: T) => void
  onError?: (error: AxiosError) => void
}

export const useSpecificEquipmentNumberSearchQuery = <T = EquipmentPlain[]>(
  equipment: EquipmentData | null,
  plantId: string,
  options?: UseSpecificEquipmentNumberSearchOptions<T>
) => {
  const {
    i18n: {language}
  } = useTranslation()
  const {enabled = true, select, onSuccess, onError} = options || {}

  const [data, setData] = useState<T | undefined>(undefined)
  const [error, setError] = useState<AxiosError | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const handleFetchSpecificEquipment = useCallback(async () => {
    if (!plantId || !equipment || !enabled) {
      if (!equipment) {
        const emptyResult = select ? select([]) : ([] as T)
        setData(emptyResult)
        setError(null)
      }
      return
    }

    setIsLoading(true)

    try {
      const path = `/plantconfig/${plantId}/equipment`
      const params = {
        params: {
          id: equipment.id,
          idType: equipment.idType,
          language
        }
      }

      const response = await ApiClient.axiosInstance.get<EquipmentPlain>(path, params)

      setData(() => {
        const equipmentArray = [response.data]
        const transformedData = select ? select(equipmentArray) : (equipmentArray as T)

        onSuccess?.(transformedData)
        setError(null)
        return transformedData
      })
    } catch (error) {
      if (axios.isAxiosError(error)) {
        setError(error)
        onError?.(error)
      } else {
        console.error('Unexpected error:', error)
      }
    } finally {
      setIsLoading(false)
    }
  }, [plantId, equipment, language, enabled, select, onSuccess, onError])

  useEffect(() => {
    if (enabled) {
      void handleFetchSpecificEquipment()
    }
  }, [enabled, handleFetchSpecificEquipment])

  return {
    data: data as T,
    error,
    isLoading,
    refetch: handleFetchSpecificEquipment
  }
}
